import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Link,
  Typography,
} from "@mui/material";

import { getData, postData } from "../../components/utilities/UtilityFunctions";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import {
  BCPScorecardForm,
  flatternFormSchema,
} from "../../components/utilities/FormUtilities";
import { hasAccess } from "../events/EventOrganize";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import ModalLoadingMessage from "../../components/modals/ModalLoadingMessage";
import { getSoftScoreMenuItems } from "../events/components/EventFunctions";
import { BCPXmark } from "../../components/icons/bcp/BCPIcons";
import { uploadBas64ToS3 } from "../../components/utilities/ImageUploader";
import PromptIfDirty from "../../components/utilities/PromptIfDirty";

export default function SoftScoreScorecard(props) {
  const { event, setEvent, setItemsMiddle } = props;
  let { softScoreId, playerId } = useParams();
  const authContext = useContext(AuthContext);

  const [valuesDirty, setValuesDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [record, setRecord] = useState(null);
  // const [event, setEvent] = useState(null);
  const [eventSoftScore, setEventSoftScore] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [schema, setSchema] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);

  const [readOnly, setReadOnly] = useState(true);

  const [loadingMessage, setLoadingMessage] = useState("Loading Player...");

  const submitForm = async (values) => {
    try {
      setProcessing(true);
      let body = {
        softScoreId,
        scorecardId: scorecard?.id,
        metaData: values,
        value: 0,
      };

      if (schema?.bcpUpdateCols?.length > 0) {
        for (let bcpUpdateCol of schema.bcpUpdateCols) {
          if (bcpUpdateCol.bcpfield?.toLowerCase() === "value") {
            body.value = values[bcpUpdateCol.id];
          }
        }
      }

      if (schema?.galleries?.length > 0) {
        for (let gallery of schema.galleries) {
          if (body.metaData?.[gallery.id]?.length > 0) {
            for (let i = 0; i < body.metaData?.[gallery.id]?.length; i++) {
              let _image = body.metaData?.[gallery.id]?.[i];
              if (
                (typeof _image === "string" || _image instanceof String) &&
                _image.startsWith("data:image/")
              ) {
                setLoadingMessage(
                  `${
                    gallery.label
                      ? `Uploading ${gallery.label} image`
                      : "Uploading image"
                  } ${i + 1} of ${body.metaData?.[gallery.id]?.length}`
                );
                // upload image to s3
                var newFile = await uploadBas64ToS3(_image);
                body.metaData[gallery.id][
                  i
                ] = `${newFile?.cloudFrontDomain}${newFile?.fileName}`;
              }
            }
          }
        }
      }

      setLoadingMessage("Saving Soft Score...");

      await postData(`/players/${playerId}/softScore`, {
        body,
      });

      setValuesDirty(false);

      postNotification("success", "Soft Score Submitted!");

      setProcessing(false);
      window.location.assign(
        `/organize/event/${event.id}?active_tab=soft-scores`
      );
    } catch (err) {
      postNotification("error", err);
    } finally {
      setProcessing(false);
      setLoadingMessage(null);
    }
  };

  useEffect(() => {
    const go = async () => {
      try {
        setLoadingMessage("Loading Player Info...");
        let _player = await getData(`/players/${playerId}`, {
          queryStringParameters: {
            // "expand[]": ["lists"],
          },
        });
        if (!_player?.eventId) {
          throw new Error("player not found");
        }

        let _metaData =
          _player?.softScores?.filter((el) => el?.id === softScoreId)?.[0]
            ?.metaData || {};
        let _record = {
          player1: { ..._player, ..._player?.user },
          metaData: _metaData,
        };
        setRecord(_record);

        setLoadingMessage("Loading Event Info...");
        let _event = await getData(`/events/${_player?.eventId}`, {
          queryStringParameters: {
            "expand[]": ["player"],
          },
        });
        if (!_event) {
          throw new Error("player not found");
        }

        try {
          var _hasAccess = await hasAccess(_event, authContext, null, false);
          setReadOnly(!_hasAccess);
        } catch (err) {
          setReadOnly(true);
        }
        setEvent(_event);
        setItemsMiddle(getSoftScoreMenuItems(_event));

        if (
          !_hasAccess &&
          !authContext?.authState?.identity?.subscriber &&
          !_event?.sponsored &&
          authContext?.authState?.identity?.userId !== _player?.userId &&
          !_event?.player?.id
        ) {
          setAccessDenied(true);
        }

        let _eventSoftScore = _event?.softScores?.filter(
          (el) => el?.id === softScoreId
        )?.[0];
        if (!_eventSoftScore) {
          throw new Error("softScoreId not found against event");
        }
        if (!_eventSoftScore?.scorecardId) {
          throw new Error("softScoreId has no attached scorecard");
        }

        setLoadingMessage("Loading Scorecard Info...");
        let _scorecard = await getData(
          `/scorecards/${_eventSoftScore?.scorecardId}`,
          {
            queryStringParameters: {
              "expand[]": ["lists"],
            },
          }
        );

        setScorecard(_scorecard);
        setEventSoftScore(_eventSoftScore);

        let formSchema = await flatternFormSchema(_scorecard, _record);
        setSchema(formSchema);

        setLoadingMessage("");
        setLoading(false);
      } catch (err) {
        postNotification("error", err);
      }
    };
    go();
    // eslint-disable-next-line
  }, [softScoreId, playerId]);

  return (
    <>
      {loadingMessage ? (
        <ModalLoadingMessage message={loadingMessage} open />
      ) : loading || processing ? (
        <ModalLoadingFull />
      ) : (
        <></>
      )}
      {accessDenied && (
        <Box sx={{ width: "100%" }}>
          <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
            <Grid
              container
              sx={{
                gap: 2,
                p: 2,
              }}
            >
              <Grid item xs={12}>
                <Typography variant="h3" gutterBottom>
                  {"401 - Access Denied"}
                </Typography>
                <Typography gutterBottom>
                  Soft scores for events you are not participating in are
                  hidden. You can unhide soft scores by becoming a{" "}
                  <Link href="/subscription" underline="none" target="_blank">
                    Subscriber
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
      {!accessDenied &&
        record &&
        event &&
        eventSoftScore &&
        scorecard &&
        schema && (
          <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
            <Grid
              container
              sx={{
                position: "relative",
              }}
            >
              <Grid item xs={12} textAlign="right" sx={{ height: "5px" }}>
                <IconButton
                  aria-label="cancel"
                  color="error"
                  href={
                    readOnly
                      ? `/event/${event.id}?active_tab=placings`
                      : `/organize/event/${event.id}?active_tab=soft-scores`
                  }
                >
                  <BCPXmark />
                </IconButton>
              </Grid>
            </Grid>

            <SoftScoreEntry
              schema={schema}
              record={record}
              submitForm={submitForm}
              readOnly={readOnly}
              processing={processing}
              setProcessing={setProcessing}
              valuesDirty={valuesDirty}
              setValuesDirty={setValuesDirty}
            />
          </Container>
        )}
    </>
  );
}

const SoftScoreEntry = ({
  schema,
  record,
  submitForm,
  readOnly,
  processing,
  setProcessing,
  valuesDirty,
  setValuesDirty,
}) => {
  const [values, setValues] = useState({});

  return (
    <>
      <PromptIfDirty
        when={valuesDirty}
        message="Are you sure you want to leave? You have unsaved changes."
      />
      <BCPScorecardForm
        schema={schema}
        record={record}
        valuesDirty={valuesDirty}
        setValuesDirty={setValuesDirty}
        submitForm={submitForm}
        readOnly={readOnly}
        values={values}
        setValues={setValues}
        setUpdating={setProcessing}
      />
      {readOnly === false && (
        <Grid container spacing={1}>
          <Grid item xs={12} sm>
            <Button
              fullWidth
              variant="contained"
              color="success"
              endIcon={
                processing ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "success.contrastText",
                    }}
                  />
                ) : null
              }
              disabled={processing || readOnly}
              onClick={() => {
                submitForm(values);
              }}
            >
              Submit Score
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};
